import { Link, useLocation } from "react-router-dom"

import DownArrowIcon from "@/assets/icons/downArrowBasic/medium.svg?react"
import LockIcon from "@/assets/icons/lockBasic/medium.svg?react"
import { cn } from "@/lib/utils.ts"

import { isNavNodeActive, isNavNodeOrChildActive, TNavbarNode } from "./utils"

export default function NavbarNode({
  isNavbarCollapsed,
  node,
  count,
  isExpanded,
  onToggle,
}: {
  node: TNavbarNode
  isNavbarCollapsed: boolean
  count: number
  /** Whether this node's submenu is currently open. Controlled by the parent section so only one
   * accordion per section can be open at a time. Ignored for nodes without children. */
  isExpanded: boolean
  /** Toggles this node's submenu open/closed. Never triggers navigation. */
  onToggle: () => void
}) {
  const { pathname } = useLocation()
  const isActiveNode = isNavNodeOrChildActive(node, pathname)

  const Icon = node.icon[isActiveNode ? "Active" : "Base"]
  const { disabled, href, externalHref, heading, children } = node

  // Disabled nodes are never expanded — children are hidden and the lock stays upright
  const showExpanded = isExpanded && !disabled
  // The submenu is only shown in the expanded sidebar; the collapsed rail never shows it.
  const childrenVisible = showExpanded && !isNavbarCollapsed

  const sharedClassName = cn([
    "relative z-10 flex w-full items-center rounded-lg p-2 text-body2 transition-colors duration-150 ease-out hover:text-neutral-title",
    isActiveNode ? "text-neutral-title" : "bg-white text-neutral-text",
    !children && !disabled && "cursor-pointer",
    isActiveNode && !children && "font-medium",
    {
      "min-h-9": !isNavbarCollapsed,
      // Icon is centred via a fixed left offset (not justify-center) because the sibling heading
      // text is pushed far off to the right (see mr-24 below) rather than removed from the flow —
      // centring the flex line itself would drag the icon along with that overflow.
      "h-[38px] rounded-full pl-[22px]": isNavbarCollapsed,
      "bg-neutral-secondBg": isActiveNode && (!children || isNavbarCollapsed),
      // pointer-events-none on every disabled row (not just leaf items) so :hover never fires —
      // the "not-allowed" cursor still shows because the outer wrapper below keeps pointer events.
      "pointer-events-none cursor-not-allowed text-neutral-disabled": disabled,
    },
  ])

  const iconEl = (
    <Icon
      className={cn(
        "inline-block h-5 w-5 shrink-0 transition-all",
        isNavbarCollapsed ? "mr-24" : "mr-1.5",
        isActiveNode ? "text-neutral-title" : disabled ? "text-neutral-disabled" : "text-neutral-text",
      )}
      style={{ transitionDuration: 250 + count * 75 + "ms" }}
    />
  )

  const chevronIndicator = !isNavbarCollapsed && children && (
    <span
      className={cn(
        "ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-500 transition-transform duration-150 ease-out",
        !disabled && (showExpanded ? "rotate-180" : "rotate-0"),
      )}
    >
      {disabled ? <LockIcon className="h-4 w-4" /> : <DownArrowIcon className="h-4 w-4" />}
    </span>
  )

  const lockIndicator = !isNavbarCollapsed && !children && disabled && (
    <span className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center text-gray-500">
      <LockIcon className="h-4 w-4" />
    </span>
  )

  const rowContent = (
    <>
      {iconEl}
      {heading}
      {chevronIndicator}
      {lockIndicator}
    </>
  )

  const row = children ? (
    isNavbarCollapsed ? (
      // Collapsed rail has no visible submenu, so the icon still acts as a direct link
      <Link
        to={href}
        onClick={(e) => {
          if (disabled) e.preventDefault()
        }}
        className={sharedClassName}
        aria-disabled={disabled}
      >
        {rowContent}
      </Link>
    ) : (
      // Expanded parent rows only ever expand/collapse their submenu — they never navigate.
      <button
        type="button"
        disabled={disabled}
        aria-expanded={showExpanded}
        onClick={() => {
          if (!disabled) onToggle()
        }}
        className={cn(sharedClassName, "text-left")}
      >
        {rowContent}
      </button>
    )
  ) : externalHref ? (
    <a
      href={externalHref}
      target="_blank"
      rel="noopener noreferrer"
      className={sharedClassName}
      aria-disabled={disabled}
    >
      {rowContent}
    </a>
  ) : (
    <Link
      to={href}
      onClick={(e) => {
        if (disabled) e.preventDefault()
      }}
      className={sharedClassName}
      aria-disabled={disabled}
    >
      {rowContent}
    </Link>
  )

  return (
    <div className={cn("relative", disabled && "cursor-not-allowed")} title={disabled ? "Disabled" : heading}>
      {row}

      {!!children && (
        <div
          aria-expanded={showExpanded}
          className={cn(
            "relative transition-all duration-150 ease-out",
            showExpanded && !isNavbarCollapsed ? "py-1 opacity-100" : "pointer-events-none py-0 opacity-0",
          )}
        >
          {children.map((child) => {
            // A child inherits its parent's lock. A locked or hidden child is never reachable: it is
            // out of the tab order and ignores clicks and Enter.
            const childDisabled = Boolean(disabled || child.disabled)
            const childUnreachable = childDisabled || !childrenVisible
            return (
              <div
                key={child.href}
                aria-hidden={!childrenVisible}
                className={cn(
                  // -mt-X must match with the height of the div
                  "ml-4 border-l transition-all duration-150 ease-out first:pt-0",
                  childrenVisible ? "mt-0 pt-1" : "-mt-8 pt-0",
                  childDisabled && "cursor-not-allowed",
                )}
              >
                <Link
                  to={child.href}
                  tabIndex={childUnreachable ? -1 : undefined}
                  onClick={(e) => {
                    if (childUnreachable) e.preventDefault()
                  }}
                  className={cn(
                    "relative ml-2 block w-[calc(100%-1rem)] rounded-md px-3 py-1.5 text-body2 transition-colors duration-150 ease-out hover:bg-neutral-secondBg hover:text-neutral-title",
                    isNavNodeActive(child, pathname)
                      ? "bg-neutral-secondBg font-medium text-neutral-title"
                      : "text-neutral-text",
                    childDisabled && "pointer-events-none text-neutral-disabled",
                  )}
                  aria-disabled={childDisabled || undefined}
                >
                  {isNavNodeActive(child, pathname) && (
                    <div className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-r-lg bg-neutral-title" />
                  )}
                  {child.heading}
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
